import { DestroyRef, inject, Injectable } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';

import { GettingStartedFacade } from '@core/getting-started-legacy';
import { GettingStartedSubtask } from '@core/getting-started-types';
import { I18nService } from '@core/i18n-legacy';
import {
  flexiblePositionStrategyFactory,
  globalPositionStrategyFactory,
  type Step,
  WalkthroughDialogComponent,
  WalkthroughFacadeService,
  WalkthroughNavigationHelperService,
  WalkthroughPostComponent,
  walkthroughSanitizedVideoResolver,
  WalkthroughStepsService,
  WalkthroughTooltipComponent,
} from '@core/walkthrough-legacy';

@Injectable({ providedIn: 'root' })
export class DisqualifyReasonsWalkthroughService {
  private readonly walkthroughStepsService = inject(WalkthroughStepsService);
  private readonly i18n = inject(I18nService);
  private readonly gettingStartedFacade = inject(GettingStartedFacade);
  private readonly walkthroughFacadeService = inject(WalkthroughFacadeService);
  private readonly navigationHelperService = inject(WalkthroughNavigationHelperService);

  private readonly destroyRef = inject(DestroyRef);

  public readonly subtask = GettingStartedSubtask.SeeHowDisqualifyReasonsWork;

  private steps: Step[] = [
    {
      id: 'walkthrough-step-disqualify-reasons-1',
      position: 1,
      component: {
        definition: WalkthroughDialogComponent,
        inputs: {
          heading: this.i18n.translate(
            'app.walkthrough.walkthroughs.disqualify_reasons.step_1.heading',
          ),
          description: this.i18n.translate(
            'app.walkthrough.walkthroughs.disqualify_reasons.step_1.description',
          ),
          videoUrl: walkthroughSanitizedVideoResolver('https://www.youtube.com/embed/MjwWdVN-884'),
          ctaText: this.i18n.translate(
            'app.walkthrough.walkthroughs.disqualify_reasons.step_1.cta_text',
          ),
        },
      },
      renderOptions: {
        strategy: globalPositionStrategyFactory,
      },
    },
    {
      id: 'walkthrough-step-disqualify-reasons-2',
      position: 2,
      component: {
        definition: WalkthroughTooltipComponent,
        inputs: {
          arrowConfig: ['middle', 'left'],
          heading: this.i18n.translate(
            'app.walkthrough.walkthroughs.disqualify_reasons.step_2.heading',
          ),
          description: this.i18n.translate(
            'app.walkthrough.walkthroughs.disqualify_reasons.step_2.description',
          ),
          ctaText: this.i18n.translate('app.onboarding.walkthrough_tooltip.next_step'),
          ctaIcon: 'chevron-right',
        },
      },
      renderOptions: {
        strategy: flexiblePositionStrategyFactory,
        useWalkthroughBackdropClass: true,
        positions: [{ originX: 'end', originY: 'center', overlayX: 'start', overlayY: 'center' }],
        offset: { offsetX: -12, offsetY: 0 },
      },
    },
    {
      id: 'walkthrough-step-disqualify-reasons-4',
      position: 3,
      component: {
        definition: WalkthroughTooltipComponent,
        inputs: {
          arrowConfig: ['middle', 'right'],
          heading: this.i18n.translate(
            'app.walkthrough.walkthroughs.disqualify_reasons.step_4.heading',
          ),
          description: this.i18n.translate(
            'app.walkthrough.walkthroughs.disqualify_reasons.step_4.description',
          ),
          ctaText: this.i18n.translate('app.onboarding.walkthrough_tooltip.next_step'),
          ctaIcon: 'chevron-right',
        },
      },
      renderOptions: {
        strategy: flexiblePositionStrategyFactory,
        useWalkthroughBackdropClass: true,
        positions: [{ originX: 'start', originY: 'center', overlayX: 'end', overlayY: 'center' }],
        offset: { offsetX: -20, offsetY: 0 },
      },
    },
    {
      id: 'walkthrough-step-disqualify-reasons-5',
      position: 4,
      component: {
        definition: WalkthroughTooltipComponent,
        inputs: {
          arrowConfig: ['middle', 'right'],
          heading: this.i18n.translate(
            'app.walkthrough.walkthroughs.disqualify_reasons.step_5.heading',
          ),
          description: this.i18n.translate(
            'app.walkthrough.walkthroughs.disqualify_reasons.step_5.description',
          ),
          ctaText: this.i18n.translate('app.onboarding.walkthrough_tooltip.next_step'),
          ctaIcon: 'chevron-right',
        },
      },
      renderOptions: {
        strategy: flexiblePositionStrategyFactory,
        useWalkthroughBackdropClass: true,
        positions: [{ originX: 'start', originY: 'center', overlayX: 'end', overlayY: 'center' }],
        offset: { offsetX: -20, offsetY: 0 },
      },
    },
    {
      id: 'walkthrough-step-disqualify-reasons-6',
      position: 5,
      component: {
        definition: WalkthroughPostComponent,
        inputs: {
          heading: this.i18n.translate(
            'app.walkthrough.walkthroughs.disqualify_reasons.step_6.heading',
          ),
          description: this.i18n.translate(
            'app.walkthrough.walkthroughs.disqualify_reasons.step_6.description',
          ),
          ctaText: this.i18n.translate(
            'app.walkthrough.walkthroughs.disqualify_reasons.step_6.cta_text',
          ),
          ctaIcon: 'open-in-new',
        },
      },
      renderOptions: {
        strategy: globalPositionStrategyFactory,
      },
      finishHandler: () => {
        this.finishHandler();
      },
    },
  ];

  public start(): void {
    this.walkthroughStepsService
      .start(this.steps)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(state => {
        this.navigationHelperService.handleWalkthroughState(state, this.subtask, [
          'settings',
          'disqualify_reasons',
        ]);
      });

    this.walkthroughStepsService.onLastStep$
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(() => {
        this.gettingStartedFacade.markSubtaskAsDone(this.subtask);
      });
  }

  private finishHandler(): void {
    this.walkthroughFacadeService.openHelpArticleLink('disqualifyReasons');
    this.navigationHelperService.navigateAndOpenDrawer(['settings', 'disqualify_reasons']);
  }
}
